import {
  type FormulaPatient,
  type OutOfRangeFlag,
  PARAMETER_FORMULAS,
  type ParameterFormula,
  type ParameterValue,
  type TestParameter,
  type UpdateReportInput,
  computeCalculatedResults,
  flagFor,
  resolveRange,
} from '#shared/types';

export type DraftDetails = UpdateReportInput['details'];

/** The editable working copy of a report's details. */
export function initialDetails(report: {
  report_details: readonly { id: number; result: string; values: readonly { id: number }[] }[];
}): DraftDetails {
  return report.report_details.map((detail) => ({
    id: detail.id,
    result: detail.result,
    value_ids: detail.values.map((value) => value.id),
  }));
}

export function withResult(details: DraftDetails, detailId: number, result: string): DraftDetails {
  return details.map((detail) => (detail.id === detailId ? { ...detail, result } : detail));
}

/** What `withCalculated` reads from the report: the patient, and each row's parameter. */
interface CalculableReport {
  patient: FormulaPatient;
  report_details: readonly {
    id: number;
    parameter: { code: string; formula: ParameterFormula | null };
  }[];
}

/**
 * The draft with every calculated row recomputed from the measured ones.
 *
 * The same function the server runs on save, so what the technician sees
 * become MCV is what gets stored. Rows that did not change keep their identity.
 */
export function withCalculated(details: DraftDetails, report: CalculableReport): DraftDetails {
  const resultById = new Map(details.map((detail) => [detail.id, detail.result]));
  const computed = computeCalculatedResults(
    report.report_details.map((source) => ({
      code: source.parameter.code,
      formula: source.parameter.formula,
      result: resultById.get(source.id) ?? '',
    })),
    report.patient,
  );
  const codeById = new Map(
    report.report_details
      .filter((source) => source.parameter.formula !== null)
      .map((source) => [source.id, source.parameter.code]),
  );

  return details.map((detail) => {
    const code = codeById.get(detail.id);
    if (code === undefined) return detail;
    const result = computed.get(code) ?? '';
    return result === detail.result ? detail : { ...detail, result };
  });
}

/** The ids of the report's calculated rows. */
export function calculatedIds(report: CalculableReport): Set<number> {
  return new Set(
    report.report_details
      .filter((source) => source.parameter.formula !== null)
      .map((source) => source.id),
  );
}

export function withRange(details: DraftDetails, detailId: number, valueId: number): DraftDetails {
  return details.map((detail) =>
    detail.id === detailId ? { ...detail, value_ids: [valueId] } : detail,
  );
}

export interface EntryProgress {
  entered: number;
  total: number;
  allEntered: boolean;
}

/**
 * Progress over the rows a technician enters. Calculated rows are left out, as
 * the server leaves them out of completion: their inputs are what is required.
 */
export function countEntered(
  details: DraftDetails,
  calculated: ReadonlySet<number> = new Set(),
): EntryProgress {
  const measured = details.filter((detail) => !calculated.has(detail.id));
  const entered = measured.filter((detail) => detail.result.trim() !== '').length;
  const total = measured.length;
  return { entered, total, allEntered: total > 0 && entered === total };
}

interface EntryPayloads {
  draft: UpdateReportInput;
  complete: UpdateReportInput;
  reopen: UpdateReportInput;
}

/** The three bodies the form can send: save as-is, complete, or reopen. */
export function buildEntryPayloads(
  draft: Pick<UpdateReportInput, 'remark_ids' | 'comment_ids' | 'details' | 'consultant_id'> & {
    /** A culture report's findings; null on any other report. */
    culture: UpdateReportInput['culture'] | null;
  },
): EntryPayloads {
  const base: UpdateReportInput = {
    remark_ids: draft.remark_ids,
    comment_ids: draft.comment_ids,
    details: draft.details,
    consultant_id: draft.consultant_id,
    ...(draft.culture ? { culture: draft.culture } : {}),
  };
  return {
    draft: base,
    complete: { ...base, report_status: 'completed' },
    reopen: { ...base, report_status: 'in_progress' },
  };
}

type EntryKeyIntent = { kind: 'complete' } | { kind: 'focus'; index: number } | null;

interface EntryKey {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
}

/**
 * What a key pressed in the result field at `index` should do.
 *
 * Ctrl/Cmd+Enter completes from anywhere in the form. A bare Enter moves
 * forward and Shift+Enter back. Arrow keys need no modifier: WCAG 2.1.4
 * constrains single-key shortcuts that are *global*, and these are scoped to a
 * focused field. `null` leaves the key alone.
 */
export function entryKeyIntent(event: EntryKey, index: number): EntryKeyIntent {
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) return { kind: 'complete' };
  if (event.key === 'Enter') {
    return { kind: 'focus', index: event.shiftKey ? index - 1 : index + 1 };
  }
  if (event.key === 'ArrowDown') return { kind: 'focus', index: index + 1 };
  if (event.key === 'ArrowUp') return { kind: 'focus', index: index - 1 };
  return null;
}

/** The stored detail behind one row: its name, snapshot and parameter. */
interface ResultRowSource {
  name: string;
  normal_range: string;
  unit: string;
  parameter?: Pick<TestParameter, 'code' | 'formula' | 'values' | 'results'>;
}

export interface ResultRowView {
  name: string;
  code: string | undefined;
  /** What to display now — the chosen cohort range, or the snapshot. */
  unit: string;
  range: string;
  /** What is currently stored on the report. */
  recordedRange: string;
  recordedUnit: string;
  presets: string[];
  ranges: ParameterValue[];
  selectedRangeId: number | undefined;
  flag: OutOfRangeFlag;
  /** The formula, as it reads, when the row is calculated rather than typed. */
  formula: string | null;
}

const MISSING_SOURCE: ResultRowSource = { name: '', normal_range: '', unit: '' };

/** The stored side of a row, with the blanks a missing detail falls back to. */
function storedDetail(source: ResultRowSource = MISSING_SOURCE) {
  const parameter = source.parameter;
  return {
    name: source.name,
    normalRange: source.normal_range,
    unit: source.unit,
    code: parameter?.code,
    ranges: parameter?.values ?? [],
    presets: (parameter?.results ?? []).map((preset) => preset.value),
    formula: parameter?.formula ?? null,
  };
}

/** Everything one row of the result grid shows, from the draft and its stored detail. */
export function describeResultRow(
  detail: DraftDetails[number],
  source: ResultRowSource | undefined,
): ResultRowView {
  const stored = storedDetail(source);
  const selectedRangeId = detail.value_ids[0];
  const shown = resolveRange(stored.ranges, selectedRangeId, {
    normal_range: source?.normal_range,
    unit: source?.unit,
  });

  return {
    name: stored.name,
    code: stored.code,
    unit: shown.unit,
    range: shown.range,
    recordedRange: stored.normalRange,
    recordedUnit: stored.unit,
    presets: stored.presets,
    ranges: stored.ranges,
    selectedRangeId,
    flag: flagFor(detail.result, shown.range),
    formula: stored.formula ? PARAMETER_FORMULAS[stored.formula].expression : null,
  };
}
